import { Check, Lock, Users, UsersRound } from 'lucide-react';
import type { DocAudience } from '@awwyeah/shared';
import type { AudienceValue } from './useDocAudience';

/**
 * "Who can see this" — three rows and, when the middle one is chosen, the roster as chips.
 *
 * Deliberately a plain block, not a sheet: every module owns its own `Sheet` (per-module
 * UI, docs/ARCHITECTURE.md), so lists drops this into its options sheet and calendar drops
 * it into the event form, and neither has to import the other's chrome.
 *
 * The copy is the design decision. "Just me" and "Everyone" are what people actually mean;
 * "private"/"family" are the words in the database and stay there.
 */

export interface AudienceMember {
  id: string;
  name: string;
  color: string;
  avatarEmoji?: string | undefined;
}

export interface AudiencePickerProps {
  value: AudienceValue;
  onChange: (next: AudienceValue) => void;
  /** The household roster, minus me (I am always in my own circle). */
  members: readonly AudienceMember[];
  /** Rendered read-only with an explanation when the caller isn't the owner. */
  disabled?: boolean;
  /** Overrides the "Everyone in the family" subtitle (the calendar says "the household"). */
  everyoneHint?: string;
  testId?: string;
}

const OPTIONS: {
  audience: DocAudience;
  label: string;
  hint: string;
  icon: JSX.Element;
}[] = [
  {
    audience: 'private',
    label: 'Just me',
    hint: 'Nobody else can open it',
    icon: <Lock className="h-5 w-5" aria-hidden="true" />,
  },
  {
    audience: 'members',
    label: 'Some people',
    hint: 'Pick who',
    icon: <Users className="h-5 w-5" aria-hidden="true" />,
  },
  {
    audience: 'family',
    label: 'Everyone',
    hint: 'The whole household',
    icon: <UsersRound className="h-5 w-5" aria-hidden="true" />,
  },
];

export function AudiencePicker({
  value,
  onChange,
  members,
  disabled = false,
  everyoneHint,
  testId = 'audience-picker',
}: AudiencePickerProps): JSX.Element {
  const select = (audience: DocAudience): void => {
    if (disabled) return;
    // Switching to "Some people" with nobody picked yet leaves the chips open and empty —
    // the server normalises an empty circle back to private, so this can't strand a doc.
    onChange({ audience, memberIds: audience === 'members' ? value.memberIds : [] });
  };

  const toggleMember = (id: string): void => {
    if (disabled) return;
    const next = value.memberIds.includes(id)
      ? value.memberIds.filter((memberId) => memberId !== id)
      : [...value.memberIds, id];
    onChange({ audience: 'members', memberIds: next });
  };

  return (
    <div className="flex flex-col" data-testid={testId} data-audience={value.audience}>
      <div className="flex flex-col overflow-hidden rounded-md border border-line">
        {OPTIONS.map((option, index) => {
          const active = value.audience === option.audience;
          return (
            <button
              key={option.audience}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              data-testid={`audience-option-${option.audience}`}
              onClick={() => select(option.audience)}
              className={`flex min-h-[44px] items-center gap-3 px-3 py-2 text-left ${
                index > 0 ? 'border-t border-line' : ''
              } ${active ? 'bg-raised' : 'bg-surface'} ${disabled ? 'opacity-60' : ''}`}
            >
              <span className={active ? 'text-accent' : 'text-fg-3'}>{option.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-fg">{option.label}</span>
                <span className="block text-[12px] text-fg-3">
                  {option.audience === 'family' ? (everyoneHint ?? option.hint) : option.hint}
                </span>
              </span>
              {active && <Check className="h-5 w-5 text-accent" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      {value.audience === 'members' && (
        <div className="pt-3" data-testid="audience-member-chips">
          {members.length === 0 ? (
            <p className="text-[13px] text-fg-3">
              Nobody else has signed in yet — they show up here as they do.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {members.map((member) => {
                const picked = value.memberIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    aria-pressed={picked}
                    disabled={disabled}
                    data-testid={`audience-member-${member.id}`}
                    onClick={() => toggleMember(member.id)}
                    className={`flex h-11 items-center gap-2 rounded-full pl-1 pr-3 text-sm font-semibold ${
                      picked
                        ? 'bg-inverse text-inverse-fg'
                        : 'border border-line bg-surface text-fg-2'
                    }`}
                  >
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-surface"
                      style={{ background: member.color }}
                      aria-hidden="true"
                    >
                      {(member.avatarEmoji ?? member.name.slice(0, 1)).toUpperCase()}
                    </span>
                    {member.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {disabled && (
        <p className="pt-3 text-[13px] text-fg-3" data-testid="audience-readonly-note">
          Only the person who made this can change who sees it.
        </p>
      )}
    </div>
  );
}

/** The little 🔒 / 👥 marker an index row wears. Renders nothing for the family default. */
export function AudienceBadge({
  audience,
  memberCount = 0,
}: {
  audience: DocAudience;
  memberCount?: number;
}): JSX.Element | null {
  if (audience === 'family') return null;
  const isPrivate = audience === 'private';
  return (
    <span
      data-testid="audience-badge"
      data-audience={audience}
      title={isPrivate ? 'Only you can see this' : `Shared with ${memberCount}`}
      className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[11px] font-semibold text-fg-3"
    >
      {isPrivate ? (
        <Lock className="h-3 w-3" aria-hidden="true" />
      ) : (
        <Users className="h-3 w-3" aria-hidden="true" />
      )}
      {isPrivate ? 'Just me' : memberCount > 0 ? `${memberCount}` : 'Shared'}
    </span>
  );
}
